import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/useT';

/**
 * The Meow / Мяу checkbox.
 *
 * A real <input type="checkbox"> underneath — keyboard, form semantics and
 * screen-reader behaviour come free; only the visual is replaced, with a
 * pip-boy style bracket that fills in when ticked.
 */
interface MeowCheckProps {
  checked: boolean;
  onChange: () => void;
  /** Full accessible label, e.g. 'Mark "Night Light" as collected'. */
  label: string;
}

const BUBBLE_MS = 900;

export function MeowCheck({ checked, onChange, label }: MeowCheckProps) {
  const { t } = useT();
  /**
   * Incremented on every toggle and used as the bubble's key, so a rapid second
   * click remounts it and restarts the animation rather than letting the first
   * one finish fading. 0 means hidden.
   */
  const [burst, setBurst] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The row can unmount while a bubble is still pending (filters, set switch).
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handleChange = (): void => {
    onChange();
    setBurst((n) => n + 1);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setBurst(0), BUBBLE_MS);
  };

  return (
    <label className="group relative inline-flex size-11 shrink-0 cursor-pointer items-center justify-center md:size-7">
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        aria-label={label}
        className="peer absolute inset-0 cursor-pointer opacity-0"
      />
      <span
        aria-hidden="true"
        className={`pointer-events-none flex size-5 items-center justify-center rounded-xs border text-[11px] leading-none font-bold transition-colors duration-150 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-pip ${
          checked
            ? 'border-pip bg-pip text-bg'
            : 'border-line-soft bg-surface-2 text-transparent group-hover:border-pip-dim'
        }`}
      >
        ✓
      </span>

      {/*
        Decorative only — aria-hidden, because the checkbox itself already
        announces the state change. pointer-events-none so it can never
        intercept a second click aimed at the box underneath it.
      */}
      {burst > 0 ? (
        <span
          key={burst}
          aria-hidden="true"
          className="meow-bubble pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 rounded-sm border border-pip-dim bg-surface-2 px-1.5 py-0.5 font-display text-[10px] leading-3 font-bold whitespace-nowrap text-pip shadow-sm shadow-black/40"
        >
          {t('meow.bubble')}
          <span
            className="absolute top-full left-1/2 -ml-[3px] size-0 border-[3px] border-transparent border-t-pip-dim"
          />
        </span>
      ) : null}
    </label>
  );
}
